import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { CheckCircle2 } from 'lucide-react'
import { calculateNights, formatDateLong } from '@hotel/shared/utils'
import { searchForRoom } from '../components/roomOccupancy'
import { PriceBreakdown } from '../components/PriceBreakdown'
import { getPrimaryImage } from '../api/client'
import { roomImageFallback } from '../components/roomImageFallbacks'
import { roomCapacityText } from '../components/roomCapacityText'
import { useBookingStatus, useCreateReservation, useCustomerLogin, useCustomerRegister, useHotelProfile } from '../api/hooks'
import type { AvailabilityResult, BookingForm, Customer, SearchState } from '../api/types'

export function BookingPage({
  customer,
  setCustomerAuth,
}: {
  customer: Customer | null
  setCustomerAuth: (next: { token: string; customer: Customer } | null) => void
}) {
  const navigate = useNavigate()
  const location = useLocation() as { state?: { room?: AvailabilityResult; search?: SearchState } }
  const room = location.state?.room
  const [search, setSearch] = useState(() => location.state?.search && room
    ? searchForRoom(location.state.search, room)
    : undefined)
  const [form, setForm] = useState<BookingForm>({
    first_name: customer?.first_name || '',
    last_name: customer?.last_name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    special_requests: '',
  })
  const [authTab, setAuthTab] = useState<'login' | 'register'>('login')
  const [authForm, setAuthForm] = useState({
    first_name: customer?.first_name || '',
    last_name: customer?.last_name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    password: '',
  })

  useEffect(() => {
    if (customer) {
      setForm((prev) => ({
        ...prev,
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email,
        phone: customer.phone,
      }))
    }
  }, [customer])

  const loginMutation = useCustomerLogin()
  const registerMutation = useCustomerRegister()
  const reservationMutation = useCreateReservation()
  const bookingStatusQuery = useBookingStatus()
  const hotelProfileQuery = useHotelProfile()

  // Fail open on the client: only an explicit false disables online booking (the server enforces).
  const onlineBookingDisabled = bookingStatusQuery.data?.online_booking_enabled === false
  const hotelPhone = hotelProfileQuery.data?.phone
  const bookingUnavailableMessage = `Las reservas en línea no están disponibles temporalmente. Por favor contáctenos por teléfono para completar su reserva.${hotelPhone ? ` Teléfono: ${hotelPhone}` : ''}`

  const reservationErrorCode = isAxiosError(reservationMutation.error)
    ? (reservationMutation.error.response?.data as { error_code?: string } | undefined)?.error_code
    : undefined

  const handleLogin = () => {
    loginMutation.mutate(
      { email: authForm.email, password: authForm.password },
      { onSuccess: (data) => { setCustomerAuth(data); setForm((prev) => ({ ...prev, ...data.customer })) } },
    )
  }

  const handleRegister = () => {
    registerMutation.mutate(authForm, {
      onSuccess: (data) => { setCustomerAuth(data); setForm((prev) => ({ ...prev, ...data.customer })) },
    })
  }

  const handleReserve = () => {
    if (!room || !search) return
    reservationMutation.mutate(
      {
        room_type_id: room.room_type_id,
        check_in_date: search.check_in,
        check_out_date: search.check_out,
        adults: search.adults,
        children: search.children,
        account_mode: customer ? 'account' : 'guest',
        special_requests: form.special_requests,
        guest: {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
        },
      },
      { onSuccess: (data) => navigate('/confirmation', { state: { reservation: data } }) },
    )
  }

  const capacityValid = Boolean(room && search && search.adults + search.children <= room.max_occupancy)
  const isValid =
    capacityValid &&
    Boolean(form.first_name.trim()) &&
    Boolean(form.last_name.trim()) &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.phone.trim().length >= 7

  if (!room || !search) return <Navigate to="/" replace />

  return (
    <main className="content-section booking-layout">
      <section className="booking-main">
        <div className="section-intro left">
          <span className="eyebrow">Reserva directa</span>
          <h1>Completa tu reserva de {room.name}</h1>
          <p>Correo y teléfono son obligatorios. Puedes reservar como invitado o con cuenta de cliente.</p>
        </div>

        <div className="form-card">
          <div className="form-grid">
            <div className="field">
              <label>Nombre</label>
              <input value={form.first_name} onChange={(e) => setForm((prev) => ({ ...prev, first_name: e.target.value }))} />
            </div>
            <div className="field">
              <label>Apellido</label>
              <input value={form.last_name} onChange={(e) => setForm((prev) => ({ ...prev, last_name: e.target.value }))} />
            </div>
            <div className="field">
              <label>Correo electrónico</label>
              <input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} />
            </div>
            <div className="field">
              <label>Teléfono</label>
              <input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} />
            </div>
            <div className="field">
              <label>Adultos</label>
              <input type="number" min={1} value={search.adults} onChange={e => setSearch(prev => prev && ({ ...prev, adults: Number(e.target.value) || 1, adultsManuallySet: true }))} />
            </div>
            <div className="field">
              <label>Niños</label>
              <input type="number" min={0} value={search.children} onChange={e => setSearch(prev => prev && ({ ...prev, children: Math.max(0, Number(e.target.value) || 0) }))} />
            </div>
            <div className="field full">
              <label>Solicitudes especiales</label>
              <textarea rows={4} value={form.special_requests} onChange={(e) => setForm((prev) => ({ ...prev, special_requests: e.target.value }))} />
            </div>
          </div>

          {!capacityValid && <div className="notice error">La habitación permite hasta {room.max_occupancy} huéspedes. Ajusta adultos y niños para continuar.</div>}
          {onlineBookingDisabled && <div className="notice error">{bookingUnavailableMessage}</div>}
          <button
            className="primary-button full-width"
            disabled={!isValid || reservationMutation.isPending || onlineBookingDisabled}
            onClick={handleReserve}
          >
            {reservationMutation.isPending ? 'Creando reserva...' : 'Crear reservación pendiente'}
          </button>
          {reservationMutation.isError && (
            <div className="notice error">
              {reservationErrorCode === 'BILLING_BLOCKED'
                ? bookingUnavailableMessage
                : 'No se pudo completar la reserva. Revisa los datos e inténtalo de nuevo.'}
            </div>
          )}
        </div>
      </section>

      <aside className="booking-sidebar">
        <div className="summary-card">
          <img src={getPrimaryImage(room.images, roomImageFallback(room.name))} alt={room.name} className="summary-image" />
          <h3>{room.name}</h3>
          <p>{roomCapacityText(room.short_description, room.max_occupancy)}</p>
          <div className="summary-row"><span>Llegada</span><strong>{formatDateLong(search.check_in)}</strong></div>
          <div className="summary-row"><span>Salida</span><strong>{formatDateLong(search.check_out)}</strong></div>
          <div className="summary-row"><span>Huéspedes</span><strong>{search.adults} adultos, {search.children} niños</strong></div>
          <div className="summary-row"><span>Noches</span><strong>{calculateNights(search.check_in, search.check_out)}</strong></div>
          <PriceBreakdown pricing={room.pricing} />
        </div>

        {!customer ? (
          <div className="auth-card">
            <div className="auth-tabs">
              <button className={authTab === 'login' ? 'active' : ''} onClick={() => setAuthTab('login')}>Ingresar</button>
              <button className={authTab === 'register' ? 'active' : ''} onClick={() => setAuthTab('register')}>Crear cuenta</button>
            </div>
            {authTab === 'register' ? (
              <div className="form-grid compact">
                <div className="field"><label>Nombre</label><input value={authForm.first_name} onChange={(e) => setAuthForm((prev) => ({ ...prev, first_name: e.target.value }))} /></div>
                <div className="field"><label>Apellido</label><input value={authForm.last_name} onChange={(e) => setAuthForm((prev) => ({ ...prev, last_name: e.target.value }))} /></div>
                <div className="field"><label>Correo</label><input type="email" value={authForm.email} onChange={(e) => setAuthForm((prev) => ({ ...prev, email: e.target.value }))} /></div>
                <div className="field"><label>Teléfono</label><input value={authForm.phone} onChange={(e) => setAuthForm((prev) => ({ ...prev, phone: e.target.value }))} /></div>
                <div className="field full"><label>Contraseña</label><input type="password" value={authForm.password} onChange={(e) => setAuthForm((prev) => ({ ...prev, password: e.target.value }))} /></div>
                <button className="secondary-button full-width" onClick={handleRegister} disabled={registerMutation.isPending}>Crear cuenta y continuar</button>
              </div>
            ) : (
              <div className="form-grid compact">
                <div className="field full"><label>Correo</label><input type="email" value={authForm.email} onChange={(e) => setAuthForm((prev) => ({ ...prev, email: e.target.value }))} /></div>
                <div className="field full"><label>Contraseña</label><input type="password" value={authForm.password} onChange={(e) => setAuthForm((prev) => ({ ...prev, password: e.target.value }))} /></div>
                <button className="secondary-button full-width" onClick={handleLogin} disabled={loginMutation.isPending}>Ingresar y usar mis datos</button>
              </div>
            )}
            {(loginMutation.isError || registerMutation.isError) && <div className="notice error">No se pudo autenticar la cuenta de cliente.</div>}
          </div>
        ) : (
          <div className="auth-card success">
            <CheckCircle2 />
            <div>
              <strong>Reservando como {customer.first_name}</strong>
              <p>Tu reserva quedará asociada a tu cuenta para verla luego en "Mi Cuenta".</p>
            </div>
          </div>
        )}
      </aside>
    </main>
  )
}
